<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "BesoksmallList",
        tabName: "Besöksmallar",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KButton from "@/components/Button.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction } from "@/components/KTable/types";
    import { Besoksmall } from "@/tabs/Oppenvard/types";
    import BesoksmallForm from "@/tabs/Admin/Oppenvard/BesoksmallForm.vue";
    import BesokstypList from "@/tabs/Admin/Oppenvard/BesokstypList.vue";
    import BesokstypkodList from "@/tabs/Admin/Oppenvard/BesokstypkodList.vue";
    import { useStore } from "@/store";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const columns: Column[] = [
        {
            header: "Namn",
            field: "namn",
            sortField: "namn",
            sortable: true,
        },
        {
            header: "Besökstyp",
            field: "typ",
            sortField: "typ__namn",
            sortable: true,
        },
        {
            header: "Typkod",
            field: "typkod",
            sortField: "typ__typkod__displayName",
            sortable: true,
        },
        {
            header: "Roll",
            field: "roller",
            contentType: "array",
            contentDirection: "vertical",
            sortField: "roller__displayName",
            sortable: true,
        },
        {
            header: "Standardtid",
            field: "standardtid",
            sortField: "standardtid",
            sortable: true,
        },
        {
            header: "Antal patienter",
            field: "antal_patienter",
            sortField: "antal_patienter",
            sortable: true,
        },
        {
            header: "Ordningsnummer",
            field: "ordning",
            sortField: "ordning",
            sortable: true,
        },
        {
            header: "Aktiv",
            field: "aktiv",
            sortField: "aktiv",
            sortable: true,
        },
    ];

    const dropdownActions: KTableAction[] = [
        {
            title: "Redigera besöksmall",
            show: () => true,
            callback: ({ data }) => {
                const besoksmall = data as Besoksmall;

                store.dispatch("tabs/openTab", {
                    component: BesoksmallForm,
                    parameters: {
                        besoksmallId: besoksmall.id,
                    },
                });
            },
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);

    const filterParams = ref<{ [key: string]: string | number | boolean } | undefined>();
    const subsetName = ref<string>("default");

    const isLoading = ref(false);

    const besoksmallar = computed<Besoksmall[]>(() => {
        return store.getters["besoksmall/besoksmallSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        }).map((bm: Besoksmall) => {
            return {
                ...bm,
                typ: bm.typ_display?.namn,
                typkod: bm.typ_display?.typkod_display?.code,
                roller: bm.roller_display.map((roll) => roll.displayName),
                aktiv: bm.aktiv ? "Ja" : "Nej",
            };
        });
    });

    const onPage = async ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        await fetchBesoksmallar({ page, limit: rows });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        const modifiedFilterParams = {
            ...(filterParams.value ?? {}),
        };

        modifiedFilterParams.ordering = sortOrder === -1 ? `-${sortField}` : sortField;

        filterParams.value = modifiedFilterParams;

        await fetchBesoksmallar({ page: 1 });
    };

    const fetchBesoksmallar = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        isLoading.value = true;

        try {
            const { total, subsetName: fetchedSubsetName } = await store.dispatch("besoksmall/fetchBesoksmallar", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta besöksmallar", "error");
        }

        isLoading.value = false;
    };

    const openCreateBesoksmall = () => {
        store.dispatch("tabs/openTab", {
            component: BesoksmallForm,
        });
    };

    const openBesokstyper = () => {
        store.dispatch("tabs/openTab", {
            component: BesokstypList,
        });
    };

    const openBesokstypkoder = () => {
        store.dispatch("tabs/openTab", {
            component: BesokstypkodList,
        });
    };

    onBeforeMount(async () => {
        await fetchBesoksmallar({ page: listPage.value });

        await store.dispatch("besoksmall/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besoksmall/removeSubscriber");
    });
</script>

<template>
    <div class="besoksmall-list">
        <admin-lock-border />
        <div class="header">
            <h2>Besöksmallar</h2>
            <KButton type="button" variant="hollow" size="slim" @click="openCreateBesoksmall">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Lägg till besöksmall</span>
            </KButton>
            <KButton type="button" variant="link" @click="openBesokstyper">
                <span>Besökstyper</span>
            </KButton>
            <KButton type="button" variant="link" @click="openBesokstypkoder">
                <span>Kodverk typkod</span>
            </KButton>
        </div>
        <KTable
            reorderableColumns
            paginator
            alwaysShowPaginator
            loadingMessage="Hämtar besöksmallar"
            paginatorPosition="bottom"
            name="besoksmall-table"
            :columns="columns"
            :actions="dropdownActions"
            :value="besoksmallar"
            :totalRecords="listCount"
            :loading="isLoading"
            @page="onPage"
            @sort="onSort"
        />
    </div>
</template>

<style lang="scss">
    .besoksmall-list {
        .header {
            h2 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
            margin-bottom: 20px;
        }
    }
</style>
